/**
 * van-landing.js
 * Shared logic for the model-landing "van-*" sections (Album 144, Album 170,
 * and future layouts).
 *
 * Loaded ONCE from theme.liquid (or from each section via
 * `{{ 'van-landing.js' | asset_url | script_tag: defer: true }}` — Shopify
 * automatically deduplicates the tag if the same asset is requested more
 * than once on the same page, so it's safe for multiple sections to request
 * it).
 *
 * Phase 2 (current): scaffolding only — each custom element is fully
 * implemented in its corresponding phase of the plan (see the phase table).
 * It's declared already so each section's HTML can use the final tag names
 * right away without having to re-register anything later.
 *
 * Rules followed (see the project's code requirements):
 * - No global variables: everything lives inside custom elements or IIFEs.
 * - No jQuery.
 * - Must re-initialize correctly when edited in the Theme Editor (Shopify
 *   fires `shopify:section:load` on the replaced section).
 */

(function () {
  'use strict';

  /* ---------------------------------------------------------------------
   * <van-image-toggle> — Section 3, Featured Systems (Phase 3 — ALREADY
   * IMPLEMENTED)
   * Toggles between two <div data-state="a|b"> inside the component when
   * clicking the .van-systems__tab buttons. Without JS, both states exist
   * in the HTML (one with `hidden`), so the essential content doesn't
   * depend on this script; this only enhances the interaction.
   * ------------------------------------------------------------------- */
  class VanImageToggle extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this._buttons = Array.prototype.slice.call(this.querySelectorAll('.van-systems__tab'));
      this._frames = {
        a: this.querySelector('[data-state="a"]'),
        b: this.querySelector('[data-state="b"]'),
      };

      this._buttons.forEach((button) => {
        button.addEventListener('click', () => this._setState(button.getAttribute('data-toggle-target')));
      });
    }

    _setState(state) {
      if (!this._frames[state]) return;

      Object.keys(this._frames).forEach((key) => {
        const isTarget = key === state;
        if (this._frames[key]) this._frames[key].hidden = !isTarget;
      });

      this._buttons.forEach((button) => {
        const isTarget = button.getAttribute('data-toggle-target') === state;
        button.classList.toggle('is-active', isTarget);
        button.setAttribute('aria-pressed', String(isTarget));
      });
    }
  }

  /* ---------------------------------------------------------------------
   * <van-color-selector> — Section 7 (Phase 5 — ALREADY IMPLEMENTED)
   * Swaps the van's main image based on the chosen color swatch, without
   * reloading the page. Without JS, only the first block's <img> is
   * rendered visible (the rest carry `hidden`) and every swatch is a real
   * <button> — so a photo of the van is always on screen even if this
   * script never runs; clicking a swatch just won't do anything yet.
   * ------------------------------------------------------------------- */
  class VanColorSelector extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this._images = Array.prototype.slice.call(this.querySelectorAll('[data-color-image]'));
      this._buttons = Array.prototype.slice.call(this.querySelectorAll('[data-color-target]'));
      this._status = this.querySelector('[data-color-status]');

      this._buttons.forEach((button) => {
        button.addEventListener('click', () => this._setColor(button.getAttribute('data-color-target')));
      });
    }

    _setColor(id) {
      let matchedLabel = '';

      this._images.forEach((img) => {
        const isTarget = img.getAttribute('data-color-image') === id;
        img.hidden = !isTarget;
      });

      this._buttons.forEach((button) => {
        const isTarget = button.getAttribute('data-color-target') === id;
        button.classList.toggle('is-active', isTarget);
        button.setAttribute('aria-pressed', String(isTarget));
        if (isTarget) matchedLabel = button.getAttribute('data-color-name') || '';
      });

      if (this._status && matchedLabel) {
        this._status.textContent = matchedLabel;
      }
    }
  }

  /* ---------------------------------------------------------------------
   * <van-systems-tabs> — Section 5 (Phase 4)
   * Accessible tabs (ARIA roles, keyboard, focus) on desktop; behaves as a
   * compact list/accordion on mobile.
   * ------------------------------------------------------------------- */
  class VanSystemsTabs extends HTMLElement {
    connectedCallback() {
      // Full implementation in Phase 4.
    }
  }

  /* ---------------------------------------------------------------------
   * <van-dimensions-toggle> — Section 8 (Phase 5 — ALREADY IMPLEMENTED)
   * Toggles between the exterior and interior panel (each panel carries its
   * own diagram AND its own measurements as plain HTML text — the numbers
   * are never only inside the image). Without JS, the exterior panel (the
   * default) is the only one visible/hidden-free, so its diagram and text
   * are both usable immediately; the interior panel becomes reachable once
   * this script runs, same accessible-comparator pattern as
   * <van-image-toggle>.
   * ------------------------------------------------------------------- */
  class VanDimensionsToggle extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this._buttons = Array.prototype.slice.call(this.querySelectorAll('[data-dim-target]'));
      this._panels = Array.prototype.slice.call(this.querySelectorAll('[data-dim-panel]'));

      this._buttons.forEach((button) => {
        button.addEventListener('click', () => this._setPanel(button.getAttribute('data-dim-target')));
      });
    }

    _setPanel(id) {
      this._panels.forEach((panel) => {
        panel.hidden = panel.getAttribute('data-dim-panel') !== id;
      });

      this._buttons.forEach((button) => {
        const isTarget = button.getAttribute('data-dim-target') === id;
        button.classList.toggle('is-active', isTarget);
        button.setAttribute('aria-pressed', String(isTarget));
      });
    }
  }

  /* ---------------------------------------------------------------------
   * <van-gallery-lightbox> — Section 9 (Phase 6 — ALREADY IMPLEMENTED)
   * Wires up PhotoSwipe v5 — the same lightbox library this theme already
   * ships and already uses for the product page's own zoom gallery (see
   * `sections/main-product.liquid`) — onto this section's plain grid of
   * <a href="full-size.jpg"><img></a> links. Reusing it means: keyboard
   * navigation (arrow keys), Escape to close, and focus trapping while
   * open all come from PhotoSwipe itself, out of the box, with no new
   * dependency added to the theme.
   *
   * The three PhotoSwipe file URLs are passed in as data-attributes from
   * Liquid (`asset_url` only resolves server-side), and loaded here with a
   * dynamic `import()` — that works fine even though this script itself is
   * a plain classic script (not `type="module"`), and it means the ~50KB
   * PhotoSwipe core is never downloaded on pages that don't have a gallery
   * on them.
   *
   * Without JavaScript (or before this finishes loading), every thumbnail
   * is still a real link to the full-size photo — clicking/tapping one
   * just opens it directly (in a new tab) instead of in the lightbox. The
   * lightbox only enhances that; it never replaces it.
   * ------------------------------------------------------------------- */
  class VanGalleryLightbox extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this._previouslyFocused = null;
      this._initLightbox();
    }

    async _initLightbox() {
      var coreUrl = this.dataset.pswpCoreUrl;
      var lightboxUrl = this.dataset.pswpLightboxUrl;
      var filtersUrl = this.dataset.pswpFiltersUrl;
      if (!coreUrl || !lightboxUrl) return;

      var self = this;

      try {
        var lightboxModulePromise = import(/* webpackIgnore: true */ lightboxUrl);
        var filtersModulePromise = filtersUrl
          ? import(/* webpackIgnore: true */ filtersUrl).catch(function () { return null; })
          : Promise.resolve(null);

        var results = await Promise.all([lightboxModulePromise, filtersModulePromise]);
        var PhotoSwipeLightbox = results[0] && results[0].default;
        var filtersModule = results[1];
        if (!PhotoSwipeLightbox) return;

        var lightbox = new PhotoSwipeLightbox({
          gallery: self,
          children: 'a',
          pswpModule: function () {
            return import(/* webpackIgnore: true */ coreUrl);
          },
        });

        if (filtersModule && typeof filtersModule.correctThumbBounds === 'function') {
          lightbox.addFilter('thumbBounds', filtersModule.correctThumbBounds);
        }

        lightbox.on('beforeOpen', function () {
          self._previouslyFocused = document.activeElement;
        });

        lightbox.on('close', function () {
          if (self._previouslyFocused) {
            self._previouslyFocused.focus({ preventScroll: true });
            self._previouslyFocused = null;
          }
        });

        lightbox.init();
        self._lightbox = lightbox;
      } catch (error) {
        /* If the PhotoSwipe assets fail to load for any reason, the plain
           <a href> links underneath still work as normal navigation — the
           gallery degrades to "click a thumbnail, see the full photo",
           never to a dead click. */
      }
    }
  }

  /* ---------------------------------------------------------------------
   * <van-spec-sheet-modal> — Section 10 (Phase 7)
   * Opens the modal with the HubSpot form. Reuses the global HubSpot Forms
   * script that theme.liquid already loads once
   * (https://js.hsforms.net/forms/embed/24287156.js) — this custom element
   * must NOT inject another HubSpot <script>, only show/hide the
   * .hs-form-frame container with its own data-form-id.
   * ------------------------------------------------------------------- */
  class VanSpecSheetModal extends HTMLElement {
    connectedCallback() {
      // Full implementation in Phase 7.
    }
  }

  const registry = {
    'van-image-toggle': VanImageToggle,
    'van-color-selector': VanColorSelector,
    'van-systems-tabs': VanSystemsTabs,
    'van-dimensions-toggle': VanDimensionsToggle,
    'van-gallery-lightbox': VanGalleryLightbox,
    'van-spec-sheet-modal': VanSpecSheetModal,
  };

  Object.keys(registry).forEach(function (tagName) {
    if (!customElements.get(tagName)) {
      customElements.define(tagName, registry[tagName]);
    }
  });
})();